// Tier feature flags — frontend mirror of supabase/functions/_shared/prices.ts
// Keep in sync when changing tier capabilities.

export type TierFeatureFlags = {
  ordVk: boolean;
  ordOzon: boolean;
  ordMediascout: boolean;
  watermarkRemoval: boolean;
  taxAutomation: boolean;
  whiteLabelExport: boolean;
  multimodalApi: boolean;
  erirAutoSync: boolean;
  agenticAuditor: boolean;
  telegramBot: boolean;
  prioritySupport: boolean;
  legalDefense: boolean;
  // Enterprise-only флаги (действуют только в /app/*)
  entWorkspaceTeam: boolean;
  entMassUploads: boolean;
  entApi: boolean;
  entWhiteLabel: boolean;
  entAuditLogs: boolean;
  entSla: boolean;
  entDedicatedManager: boolean;
  entCustomIntegrations: boolean;
};

export const TIER_UNLIMITED = -1;
export const TIER_TOKEN_LIMITS: Record<string, number> = {
  free: 0,
  trial: 5,
  blogger_lite: 30,
  pro: TIER_UNLIMITED,
  agency: TIER_UNLIMITED,
  ent_starter: 400,
  ent_agency: TIER_UNLIMITED,
  ent_enterprise: TIER_UNLIMITED,
  ent_white_label: TIER_UNLIMITED,
};

export function getTierTokenLimit(tier: string | null | undefined): number {
  if (!tier) return 0;
  return TIER_TOKEN_LIMITS[tier] ?? 0;
}

const ENT_FALSE = {
  entWorkspaceTeam: false,
  entMassUploads: false,
  entApi: false,
  entWhiteLabel: false,
  entAuditLogs: false,
  entSla: false,
  entDedicatedManager: false,
  entCustomIntegrations: false,
};

export function getTierFeatures(tier: string | null | undefined): TierFeatureFlags {
  switch (tier) {
    case "trial":
      return {
        ordVk: true, ordOzon: false, ordMediascout: false,
        watermarkRemoval: true, taxAutomation: true, whiteLabelExport: false,
        multimodalApi: false, erirAutoSync: false, agenticAuditor: true,
        telegramBot: false, prioritySupport: false, legalDefense: false,
        ...ENT_FALSE,
      };
    case "blogger_lite":
      return {
        ordVk: true, ordOzon: false, ordMediascout: false,
        watermarkRemoval: true, taxAutomation: true, whiteLabelExport: false,
        multimodalApi: false, erirAutoSync: false, agenticAuditor: true,
        telegramBot: true, prioritySupport: false, legalDefense: false,
        ...ENT_FALSE,
      };
    case "pro":
      return {
        ordVk: true, ordOzon: true, ordMediascout: true,
        watermarkRemoval: true, taxAutomation: true, whiteLabelExport: false,
        multimodalApi: false, erirAutoSync: true, agenticAuditor: true,
        telegramBot: true, prioritySupport: true, legalDefense: false,
        ...ENT_FALSE,
      };
    case "agency":
      return {
        ordVk: true, ordOzon: true, ordMediascout: true,
        watermarkRemoval: true, taxAutomation: true, whiteLabelExport: true,
        multimodalApi: true, erirAutoSync: true, agenticAuditor: true,
        telegramBot: true, prioritySupport: true, legalDefense: true,
        ...ENT_FALSE,
      };
    case "ent_starter":
      return {
        ordVk: true, ordOzon: false, ordMediascout: false,
        watermarkRemoval: true, taxAutomation: false, whiteLabelExport: false,
        multimodalApi: false, erirAutoSync: false, agenticAuditor: true,
        telegramBot: true, prioritySupport: false, legalDefense: false,
        ...ENT_FALSE,
      };
    case "ent_agency":
      return {
        ordVk: true, ordOzon: true, ordMediascout: true,
        watermarkRemoval: true, taxAutomation: true, whiteLabelExport: false,
        multimodalApi: true, erirAutoSync: true, agenticAuditor: true,
        telegramBot: true, prioritySupport: true, legalDefense: false,
        ...ENT_FALSE,
        entWorkspaceTeam: true, entMassUploads: true, entApi: true,
      };
    case "ent_enterprise":
      return {
        ordVk: true, ordOzon: true, ordMediascout: true,
        watermarkRemoval: true, taxAutomation: true, whiteLabelExport: true,
        multimodalApi: true, erirAutoSync: true, agenticAuditor: true,
        telegramBot: true, prioritySupport: true, legalDefense: true,
        entWorkspaceTeam: true, entMassUploads: true, entApi: true,
        entWhiteLabel: true, entAuditLogs: true, entSla: true,
        entDedicatedManager: true, entCustomIntegrations: true,
      };
    case "ent_white_label":
      return {
        ordVk: true, ordOzon: true, ordMediascout: true,
        watermarkRemoval: true, taxAutomation: true, whiteLabelExport: true,
        multimodalApi: true, erirAutoSync: true, agenticAuditor: true,
        telegramBot: true, prioritySupport: true, legalDefense: true,
        entWorkspaceTeam: true, entMassUploads: true, entApi: true,
        entWhiteLabel: true, entAuditLogs: true, entSla: true,
        entDedicatedManager: true, entCustomIntegrations: true,
      };
    default:
      return {
        ordVk: false, ordOzon: false, ordMediascout: false,
        watermarkRemoval: false, taxAutomation: true, whiteLabelExport: false,
        multimodalApi: false, erirAutoSync: false, agenticAuditor: false,
        telegramBot: false, prioritySupport: false, legalDefense: false,
        ...ENT_FALSE,
      };
  }
}

export type FeatureKey = keyof TierFeatureFlags;

// ────────────────────────────────────────────────────────────────────────
// Public-facing feature bullets — единый источник истины для /pricing
// и для карточки «Что входит в ваш тариф» в личном кабинете.
// ────────────────────────────────────────────────────────────────────────
export type TierBullet = { text: string; aiInsight?: string };

export const TIER_PUBLIC_FEATURES: Record<
  "trial" | "blogger_lite" | "pro" | "agency" | "ent_starter" | "ent_agency" | "ent_enterprise" | "ent_white_label",
  TierBullet[]
> = {
  trial: [
    { text: "5 бесплатных erid-токенов через VK ORD" },
    { text: "ИИ-аудит креативов (ФЗ-168)" },
    { text: "Маркировка постов без водяных знаков" },
    { text: "Без привязки карты, бессрочно" },
  ],
  blogger_lite: [
    { text: "30 erid-токенов / мес. (только VK ORD)" },
    { text: "ИИ-аудит креативов (ФЗ-168)" },
    { text: "Маркировка постов без водяных знаков" },
    { text: "Telegram-бот для уведомлений" },
    { text: "Отчёты в ЕРИР по запросу" },
  ],
  pro: [
    { text: "Безлимит erid-токенов (VK ORD + Ozon ORD)" },
    { text: "Авто-документы: договор и акт в один клик" },
    { text: "Автоматический ежемесячный отчёт в ЕРИР" },
    { text: "Автоматизация 3% сбора по 479-ФЗ" },
    { text: "Лингвистический ИИ-аудит ФЗ-168" },
    { text: "Маркировка постов без водяных знаков" },
    { text: "Telegram-бот для уведомлений" },
    { text: "Приоритетная поддержка" },
  ],
  agency: [
    { text: "Безлимит erid-токенов + общий пул для команды" },
    { text: "Мульти-аккаунт и роли (Admin / Менеджер)" },
    { text: "White-label экспорт отчётов" },
    { text: "Multimodal API (изображения, видео, кружки)" },
    { text: "Авто-сверка с ЕРИР каждые 10 дней" },
    { text: "Юридическая защита: спор с ФАС/РКН" },
    { text: "Персональный менеджер и приоритетная поддержка" },
  ],
  ent_starter: [
    { text: "400 ERID / мес" },
    { text: "Базовый AI Scanner" },
    { text: "Telegram bot" },
    { text: "1 workspace" },
    { text: "Basic analytics" },
  ],
  ent_agency: [
    { text: "Безлимит ERID" },
    { text: "AI Compliance" },
    { text: "Mass uploads" },
    { text: "Team access" },
    { text: "Расширенная аналитика" },
    { text: "API-доступ" },
    { text: "Priority support" },
  ],
  ent_enterprise: [
    { text: "White Label" },
    { text: "Dedicated infrastructure" },
    { text: "Enterprise API" },
    { text: "Custom integrations" },
    { text: "SLA 99.9%" },
    { text: "Audit logs" },
    { text: "Compliance Center" },
    { text: "Персональный менеджер" },
  ],
  ent_white_label: [
    { text: "Собственный бренд" },
    { text: "Свой домен" },
    { text: "Свой dashboard" },
    { text: "Свой API" },
    { text: "Multi-tenant система" },
  ],
};

export const TIER_LABELS: Record<string, string> = {
  trial: "Промо «Профи»",
  blogger_lite: "Старт",
  pro: "Бизнес",
  agency: "Агентство",
  ent_starter: "Starter",
  ent_agency: "Agency",
  ent_enterprise: "Enterprise",
  ent_white_label: "White Label",
};
